import React from "react";
import { Spinner, Callout, Button, FormGroup, InputGroup } from "@blueprintjs/core";
import { useAuth } from "../../auth/AuthContext";
import { useOrg } from "../../auth/OrgContext";
import { useProjects } from "../../auth/ProjectContext";
import { useProjectData } from "../../projects/ProjectDataContext";

const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "") + "/api";

export default function ProjectOverview() {
  const { idToken } = useAuth();
  const { currentOrg } = useOrg();
  const { currentProject } = useProjects();
  const { projects, updateProject, addTask, updateTask } = useProjectData();
  const pmProject = projects.find((p) => p.id === currentProject?.id);
  const [projName, setProjName] = React.useState(pmProject?.name || "");
  const [projType, setProjType] = React.useState(pmProject?.project_type || "");
  const [taskForm, setTaskForm] = React.useState<{ id?: string; name: string; due: string }>(
    { name: "", due: "" }
  );
  const [loading, setLoading] = React.useState(false);
  const [members, setMembers] = React.useState<{ id: string; email: string; role: string }[]>([]);

  React.useEffect(() => {
    if (pmProject) {
      setProjName(pmProject.name || "");
      setProjType(pmProject.project_type || "");
    }
  }, [pmProject]);

  React.useEffect(() => {
    if (!idToken || !currentOrg || !currentProject) return;
    fetch(`${API_BASE}/orgs/${currentOrg.id}/projects/${currentProject.id}/members`, {
      headers: { Authorization: `Bearer ${idToken}` },
      credentials: 'include',
    })
      .then(res => (res.ok ? res.json() : []))
      .then(setMembers)
      .catch(() => setMembers([]));
  }, [idToken, currentOrg, currentProject, setMembers]);

  React.useEffect(() => {
    if (!idToken || !currentOrg || !currentProject) return;
    setLoading(true);
    fetch(`${API_BASE}/orgs/${currentOrg.id}/projects/${currentProject.id}`, {
      headers: { Authorization: `Bearer ${idToken}` },
      credentials: "include",
    })
      .then(async (res) => {
        if (res.ok) {
          // const data = await res.json();
          // setProject(data);
          await res.json(); // consume the body to avoid memory leaks
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [idToken, currentOrg, currentProject, setLoading]);

  if (!currentProject) {
    return <Callout intent="warning">Select a project from the switcher.</Callout>;
  }

  if (loading) return <Spinner size={30} style={{ margin: 40 }} />;

  const saveProject = () => {
    if (currentProject) {
      updateProject(currentProject.id, { name: projName, project_type: projType });
    }
  };

  const saveTask = () => {
    if (!currentProject) return;
    if (taskForm.id) {
      updateTask(currentProject.id, taskForm.id, { name: taskForm.name, dueDate: taskForm.due });
    } else {
      addTask(currentProject.id, { name: taskForm.name, dueDate: taskForm.due, status: "To Do" });
    }
    setTaskForm({ name: "", due: "" });
  };

  return (
    <div style={{ maxWidth: 700, margin: "0 auto", padding: "2rem 0" }}>
      <h2 style={{ marginBottom: 16 }}>Project Settings</h2>
      <FormGroup label="Name">
        <InputGroup value={projName} onChange={(e) => setProjName(e.target.value)} />
      </FormGroup>
      <FormGroup label="Type">
        <InputGroup value={projType} onChange={(e) => setProjType(e.target.value)} />
      </FormGroup>
      <Button intent="primary" onClick={saveProject}>Save Project</Button>

      {members.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h3>Project Members</h3>
          <ul>
            {members.map(m => (
              <li key={m.id}>{m.email} - {m.role}</li>
            ))}
          </ul>
        </div>
      )}

      <h3 style={{ marginTop: 32 }}>Tasks</h3>
      <ul>
        {pmProject?.tasks.map((t) => (
          <li key={t.id} style={{ marginBottom: 4 }}>
            {t.name} {t.dueDate && `(${t.dueDate})`}
            <Button minimal onClick={() => setTaskForm({ id: t.id, name: t.name, due: t.dueDate || "" })}>
              Edit
            </Button>
          </li>
        ))}
      </ul>

      <FormGroup label="Task Name">
        <InputGroup value={taskForm.name} onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })} />
      </FormGroup>
      <FormGroup label="Due Date">
        <InputGroup type="date" value={taskForm.due} onChange={(e) => setTaskForm({ ...taskForm, due: e.target.value })} />
      </FormGroup>
      <Button intent="primary" onClick={saveTask} disabled={!taskForm.name.trim()} style={{ marginRight: 8 }}>
        {taskForm.id ? "Update Task" : "Add Task"}
      </Button>
      {taskForm.id && (
        <Button onClick={() => setTaskForm({ name: "", due: "" })}>
          Cancel
        </Button>
      )}
    </div>
  );
}
